import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import CommonTable from "pages/Services/ServiceDetails/Impacts/CommonTable";
import { TableContainer, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import ServiceStages from "./ServiceStages";

const useStyles = makeStyles()((theme) => ({
	headerText: {
		marginTop: 10,
		fontWeight: 800,
		fontSize: 20,
		marginBottom: "5px",
	},
	tableContainer: {
		marginTop: 40,
	},
	tableHead: {
		backgroundColor: "#D2D2D9",
		border: "1px solid",
	},
	noDataTableRow: {
		borderBottom: "none !important",
	},
	footerLabel: {
		fontWeight: 600,
		fontSize: 15,
		marginBottom: "3px",
	},
	footerText: {
		fontWeight: 400,
		fontSize: "0.875rem",
		marginBottom: "3px",
	},
	main: {
		display: "flex",
		flexDirection: "column",
	},
	childClass: {
		display: "flex",
		margin: "5px 0",
	},
	headingText: {
		width: 100,
	},
}));

export default function ServiceReport({
	completedService: rows,
	roleName,
	customCaptions,
	formatQuestion,
	groupByStage,
	isPrint,
	onImageClick,
}) {
	const { classes, cx } = useStyles();
	const { t } = useTranslation();
	return (
		<>
			<TableContainer>
				<Table aria-label="collapsible table">
					<TableBody>
						{rows?.map((row) => (
							<React.Fragment>
								<span style={{ fontWeight: "800px", marginTop: "-10px" }}>
									{t("message.sessionStartedByAt", {
										name: row?.displayName,
										date: row.startDate && row?.startDate,
									})}
								</span>
								<TableRow
									key={row.id}
									sx={{ "& > *": { borderBottom: "unset" } }}
								>
									<TableCell
										className={classes.noDataTableRow}
										component="th"
										scope="row"
									>
										{row.displayName}
									</TableCell>
									<TableCell align="center">
										{row.startDate && row?.startDate}
									</TableCell>
									<TableCell align="center">
										{row.endDate && row?.endDate}
									</TableCell>
								</TableRow>
								{row?.sessionQuestion?.filter((d) => d?.timing === "B")
									?.length > 0 && (
									<TableRow>
										<TableCell colSpan={10}>
											<Typography className={classes.headerText}>
												{t("label.startQuestions")}
											</Typography>
											<CommonTable
												headers={[
													t("label.question"),
													t("label.response"),
													t("label.completedBy"),
													t("label.completionTime"),
												]}
												columns={["caption", "response", "displayName", "date"]}
												isPrint={isPrint}
												data={formatQuestion(
													row?.sessionQuestion?.filter(
														(d) => d?.timing === "B"
													),
													row?.displayName
												)}
											/>
										</TableCell>
									</TableRow>
								)}
								{row?.stages?.length > 0 && (
									<TableRow>
										<TableCell colSpan={18}>
											<ServiceStages
												completedBy={row?.displayName}
												tasks={row?.stages}
												customCaptions={customCaptions}
												formatQuestion={formatQuestion}
												showSkipped
												onImageClick={onImageClick}
											/>
										</TableCell>
									</TableRow>
								)}

								{row?.pauses?.length > 0 && (
									<TableRow>
										<TableCell colSpan={18}>
											<CommonTable
												headers={[
													t("label.pause"),
													t("label.pauseSubcategory"),
													t("label.otherReason"),
													`${customCaptions?.user}`,
													t("label.startDate"),
													t("label.endDate"),
													t("label.duration"),
												]}
												columns={[
													"pauseReason",
													"pauseSubcategory",
													"pauseOtherReason",
													"displayName",
													"startDate",
													"endDate",
													"duration",
												]}
												isPrint={isPrint}
												data={row.pauses?.map((x) => ({
													...x,
													startDate: x.startDate,
													endDate: x.endDate,
													duration: t("label.countMins", {
														count: x?.pauseDuration,
													}),
													displayName: row?.displayName,
												}))}
											/>
										</TableCell>
									</TableRow>
								)}

								{row?.sessionQuestion?.filter((d) => d?.timing === "E")
									?.length > 0 && (
									<TableRow>
										<TableCell colSpan={10}>
											<Typography className={classes.headerText}>
												{t("label.endQuestions")}
											</Typography>
											<CommonTable
												headers={[
													t("label.question"),
													t("label.response"),
													t("label.completedBy"),
													t("label.completionTime"),
												]}
												columns={["caption", "response", "displayName", "date"]}
												data={formatQuestion(
													row?.sessionQuestion?.filter(
														(d) => d?.timing === "E"
													),
													row?.displayName
												)}
											/>
										</TableCell>
									</TableRow>
								)}
								{row.endDate && (
									<React.Fragment>
										<Typography className={classes.headerText}>
											{t("message.endOfEntitySession", {
												entity: customCaptions?.service,
											})}{" "}
										</Typography>
										<TableRow>
											<TableCell colSpan={10}>
												<Grid container spacing={2} alignItems="center">
													<Grid item xs={6}>
														<div className={classes.main}>
															<div className={classes.childClass}>
																<Typography
																	className={cx(
																		classes.headingText,
																		classes.footerLabel
																	)}
																	component="span"
																>
																	{t("label.name")}
																</Typography>
																<Typography
																	className={cx(
																		classes.responseData,
																		classes.footerText
																	)}
																	component="span"
																>
																	{row?.displayName}
																</Typography>
															</div>
															<div className={classes.childClass}>
																<Typography
																	className={cx(
																		classes.headingText,
																		classes.footerLabel
																	)}
																	component="span"
																>
																	{t("entity.role")}
																</Typography>
																<Typography
																	className={cx(
																		classes.responseData,
																		classes.footerText
																	)}
																	component="span"
																>
																	{roleName}
																</Typography>
															</div>
															<div className={classes.childClass}>
																<Typography
																	className={cx(
																		classes.headingText,
																		classes.footerLabel
																	)}
																	component="span"
																>
																	{t("label.date")}
																</Typography>
																<Typography
																	className={cx(
																		classes.responseData,
																		classes.footerText
																	)}
																	component="span"
																>
																	{row.endDate}
																</Typography>
															</div>
														</div>
													</Grid>

													{row.signatureURL && (
														<Grid item xs={6}>
															<div>
																<img
																	src={row.signatureURL}
																	style={{
																		width: "100%",
																		height: "100%",
																		maxHeight: "400px",
																		maxWidth: "600px",
																		objectFit: "contain",
																	}}
																	alt="signature"
																/>
															</div>
														</Grid>
													)}
												</Grid>
											</TableCell>
										</TableRow>
									</React.Fragment>
								)}
							</React.Fragment>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
}
